import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { Cart } from "../../../q-commerce/models/cart.model";
import { ButtonConfig, Child, GridChildViewConfig, Modifier, Variant } from "../../../q-commerce/models/category-group.model";
import { ServicesService } from "../../../service/services/services.service";
import { EDeliveryService } from "../../services/e-delivery.service";

@Component({
  selector: "app-product-repeat-last-customization-modal",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./product-repeat-last-customization-modal.component.html",
  styleUrl: "./product-repeat-last-customization-modal.component.scss"
})
export class ProductRepeatLastCustomizationModalComponent {
  @Input() gridChildViewConfig!: GridChildViewConfig | null;
  cartItemVariants: Cart[] = [];
  selectedProduct!: Child;
  lastCustomizationModalRef!: NgbModalRef;
  modalConfig: any = { size: "md", centered: true, backdrop: "static" };
  @ViewChild("lastCustomizationModal", { static: true }) lastCustomizationModal!: TemplateRef<any>;
  @Output() openProductCustomizationModal = new EventEmitter<Child>();

  constructor(private modalService: NgbModal, public helper: Helper, private edeliveryService: EDeliveryService, private servicesService: ServicesService) {}

  /**
   * Opens the modal for repeating last customization of a product.
   * Retrieves and populates the variants of the product that are already in the cart.
   * @param product The product to repeat last customization for.
   */
  openModal(product: Child): void {
    this.lastCustomizationModalRef = this.modalService.open(this.lastCustomizationModal, this.modalConfig);
    this.selectedProduct = product;
    const cartItems = this.getCartItems();
    this.cartItemVariants = cartItems.filter((item: Cart) => item.productId === product._id);
  }

  /**
   * Retrieves the name of a product variant, in the format of "value unitType".
   * @param variantId The ID of the variant to get the name for.
   * @returns The name of the specified variant.
   */
  getVariantName(variantId: string): string {
    const variant = this.selectedProduct.variants.find((variantItem: Variant) => variantItem._id === variantId);
    if (this.selectedProduct.variantType === this.helper.VariantType.SIZE) {
      return variant ? variant.unit.unitDescription : "";
    }
    return variant ? `${variant.unit.unitValue} ${this.helper.translateService.instant(this.helper.UnitTypeString[variant.unit.unitType])}` : "";
  }

  /**
   * Returns the total price of a customized product variant.
   * @param variant The customized variant to get the total price of.
   * @returns The total price of the customized variant, including all modifiers.
   */
  getPriceOfCustomizedVariant(variant: Cart): number {
    return (
      (variant.price ?? 0) +
      (variant.modifiers
        ? variant.modifiers.reduce((total, modifier) => total + modifier.options.reduce((optionTotal, option) => optionTotal + option.price * option.qty, 0), 0)
        : 0)
    );
  }

  getTotalProductVariantQty(variant: Cart): number | undefined {
    const cartItems = this.getCartItems();
    return cartItems.find((item: Cart) => item.variantId === variant.variantId && JSON.stringify(item.modifiers) === JSON.stringify(variant.modifiers))?.qty;
  }

  /**
   * Returns the total price of a customized product variant.
   * @param variantId The ID of the variant to get the total price of.
   * @param variant The customized variant.
   * @returns The total price of the customized variant.
   */
  getTotalProductVariantPrice(variant: Cart): number {
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => item.variantId === variant.variantId && JSON.stringify(item.modifiers) === JSON.stringify(variant.modifiers));
    return cartItem ? this.getPriceOfCustomizedVariant(variant) * cartItem.qty : 0;
  }

  /**
   * Closes the current modal and emits an event to open the product customization modal
   * with the currently selected product.
   */
  openCustomizationModal(): void {
    this.closeModal();
    this.openProductCustomizationModal.emit(this.selectedProduct);
  }

  /**
   * Increments the quantity of a product variant in the cart by 1.
   * If the product variant is not in the cart, it does not do anything.
   * @param variantId The ID of the product variant to increment the quantity of.
   */
  incrementProductQuantity(cartProduct: Cart): void {
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => item.variantId === cartProduct.variantId && JSON.stringify(item.modifiers) === JSON.stringify(cartProduct.modifiers));
    const variant = this.selectedProduct.variants.find((variantItem: Variant) => variantItem._id === cartProduct.variantId);
    if (variant && cartItem) {
      if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.servicesService.updateQuantity(this.selectedProduct._id, cartProduct, cartItem.qty + 1, variant.maxQtyAddInCart);
      } else if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.edeliveryService.updateQuantity(this.selectedProduct._id, cartProduct, cartItem.qty + 1, variant.maxQtyAddInCart);
      }
    }
  }

  /**
   * Decrements the quantity of a product variant in the cart by 1.
   * If the product variant is not in the cart or the quantity is already 1, it removes the product variant from the cart.
   * @param variantId The ID of the product variant to decrement the quantity of.
   * @param index The index of the product variant in the cartItemVariants array.
   */
  decrementProductQuantity(cartProduct: Cart, index: number): void {
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => item.variantId === cartProduct.variantId && JSON.stringify(item.modifiers) === JSON.stringify(cartProduct.modifiers));
    const variant = this.selectedProduct.variants.find((variantItem: Variant) => variantItem._id === cartProduct.variantId);
    if (variant && cartItem && cartItem.qty > 1) {
      if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.servicesService.updateQuantity(this.selectedProduct._id, cartProduct, cartItem.qty - 1, variant.maxQtyAddInCart);
      } else if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.edeliveryService.updateQuantity(this.selectedProduct._id, cartProduct, cartItem.qty - 1, variant.maxQtyAddInCart);
      }
    } else {
      if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.servicesService.removeFromCart(this.selectedProduct._id, cartProduct);
      } else if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.edeliveryService.removeFromCart(this.selectedProduct._id, cartProduct);
      }
      this.cartItemVariants.splice(index, 1);
      if (this.cartItemVariants.length === 0) {
        this.closeModal();
      }
    }
  }

  /**
   * Retrieves the maximum quantity that can be added to the cart for a specific variant.
   * @param variantId - The ID of the variant to retrieve the maximum cart quantity for.
   * @returns The maximum quantity allowed in the cart for the specified variant, or undefined if the variant is not found.
   */
  getVariantMaxQtyAddInCart(variantId: string): number | undefined {
    const variant = this.selectedProduct.variants.find((variantItem: Variant) => variantItem._id === variantId);
    return variant?.maxQtyAddInCart;
  }

  formatModifiers(item: any): string {
    if (!item.modifiers || item.modifiers.length === 0) return "";
    return item.modifiers.map((modifier: Modifier) => modifier.options.map((option) => (option.qty > 1 ? `${option.name} x ${option.qty}` : option.name)).join(", ")).join(", ");
  }

  private getCartItems(): Cart[] {
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      return JSON.parse(localStorage.getItem("cartItems") || "[]");
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      return JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    }
    return JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
  }

  /**
   * Retrieves the total quantity of a product variant in the cart.
   * @param variantId The ID of the variant to retrieve the total quantity of.
   * @returns The total quantity of the specified variant in the cart.
   */
  getTotalProductVariantQtyinCart(variantId: string): number {
    const cartItems = this.getCartItems();
    return cartItems.filter((item: Cart) => item.variantId === variantId).reduce((total, item) => total + item.qty, 0);
  }

  /**
   * Closes the last customization modal and dismisses the modal reference.
   */
  closeModal(): void {
    this.lastCustomizationModalRef?.dismiss(this.lastCustomizationModal);
  }

  /**
   * Returns the CSS style object for a button based on its configuration.
   * @param buttonConfig The button configuration object.
   * @returns An object with CSS styles for the button, including background color and color.
   */
  getButtonStyle(buttonConfig: ButtonConfig): { [key: string]: string } {
    return {
      backgroundColor: buttonConfig?.bgColor,
      color: buttonConfig?.color
    };
  }
}
